// The "Parked" page (2026-10-02, Lab-only behind PARKING_ENABLED): contacts
// their owners parked to come back to. A csr sees one list, their own. An
// admin sees a split screen: "My parked contacts" on top, "All parked
// contacts" (every agent's, the admin's included) below, which the AppBar's
// Parked Contacts Alert scrolls to. Clicking a row opens the contact; for its
// owner, opening it unparks it.
import { useEffect, useMemo, useState } from 'react'
import { Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell, Chip, TextField, Select, MenuItem, Checkbox, ListItemText, FormControl, InputLabel, Link, Stack } from '@mui/material'
import { intentLabel } from './constants/intents.js'
import { COMPANY_TAG_STYLE } from './constants/companies.js'
import { formatParkedDuration, parkedRowCustomer, sortParksLongestFirst, isParkOverdue, PARK_ALERT_MINUTES, parkFilterOptions, parkMatchesFilters, prunedParkFilters, hasParkFilters, readParkFilters, writeParkFilters, EMPTY_PARK_FILTERS } from './parking.js'

export const ALL_PARKED_SECTION_ID = 'all-parked-contacts'

function useNow(intervalMs = 1000) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs)
    return () => clearInterval(id)
  }, [intervalMs])
  return now
}

// One park's fields, as the Parked tab shows them: { label, value, testId,
// sx } in column order. Shared with the auto re-queue notice
// (AutoRequeueNotice.jsx), so the two always show the same things.
// durationMs: "Parked for" (live for an open park, the full length for a
// closed one).
export function parkFields(p, { durationMs, showParkedBy = false } = {}) {
  const c = p.conversation ?? {}
  return [
    { label: 'ID', value: c.convNumber ?? '—' },
    { label: 'Order', value: c.erpOrderId ?? '—' },
    { label: 'Customer', value: parkedRowCustomer(c) ?? '—' },
    { label: 'Intent', value: c.intent ? intentLabel(c.intent) : '—' },
    {
      label: 'Company · Client',
      value: c.companyCode
        ? <Chip size="small" label={`${c.companyCode} · ${c.client ?? c.channel ?? ''}`} sx={{ ...(COMPANY_TAG_STYLE[c.companyCode] ?? {}), fontWeight: 700, fontSize: '0.75rem', height: 20, '& .MuiChip-label': { px: 0.75 } }} />
        : '—',
    },
    ...(showParkedBy ? [{ label: 'Parked by', value: p.agentDisplayName ?? p.agent }] : []),
    { label: 'Parked at', value: new Date(p.parkedAt).toLocaleString() },
    { label: 'Parked for', value: formatParkedDuration(durationMs), testId: 'parked-for' },
    { label: 'Note', value: p.note ?? <em>no note</em>, sx: { maxWidth: 320, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' } },
  ]
}

// overdueMinutes (2026-10-08, admin "All" section only): rows parked past it
// render their text red, the same comparison as the header alert's count.
function ParkedTable({ parks, showParkedBy, onOpen, now, testId, overdueMinutes = null }) {
  if (!parks.length) {
    return <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No parked contacts.</Typography>
  }
  const rowFields = (p) => parkFields(p, { durationMs: now - new Date(p.parkedAt).getTime(), showParkedBy })
  return (
    <Table size="small" data-testid={testId}>
      <TableHead>
        <TableRow>
          {rowFields(parks[0]).map((f) => <TableCell key={f.label}>{f.label}</TableCell>)}
        </TableRow>
      </TableHead>
      <TableBody>
        {parks.map((p) => {
          const overdue = overdueMinutes != null && isParkOverdue(p, overdueMinutes, now)
          return (
            <TableRow
              key={p.id}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => onOpen?.(p.conversationId)}
              data-testid="parked-row"
              data-overdue={overdue ? 'true' : undefined}
            >
              {rowFields(p).map((f) => <TableCell key={f.label} data-testid={f.testId} sx={{ ...(f.sx ?? {}), ...(overdue ? { color: 'error.main' } : {}) }}>{f.value}</TableCell>)}
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

// One multi-select filter, "All …" when nothing is chosen.
function FilterSelect({ label, allLabel, options, value, onChange, testId }) {
  const labelOf = (v) => options.find((o) => o.value === v)?.label ?? v
  return (
    <FormControl size="small" sx={{ minWidth: 160 }}>
      <InputLabel shrink>{label}</InputLabel>
      <Select
        multiple displayEmpty label={label} value={value}
        onChange={(e) => onChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
        renderValue={(sel) => (sel.length ? sel.map(labelOf).join(', ') : allLabel)}
        SelectDisplayProps={{ 'data-testid': testId }}
      >
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value} dense>
            <Checkbox size="small" checked={value.includes(o.value)} />
            <ListItemText primary={o.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

// "All parked contacts" (admin only): Agent / Company / Client filters over
// the loaded rows (client-side, AND, empty = all), remembered per admin;
// longest parked first; red past the alert threshold (2026-10-08).
function AllParkedSection({ parks, onOpen, now, alertMinutes, adminKey }) {
  const options = useMemo(() => parkFilterOptions(parks), [parks])
  const [filters, setFilters] = useState(() => readParkFilters(adminKey))
  const pruned = useMemo(() => prunedParkFilters(filters, options), [filters, options])
  useEffect(() => {
    if (JSON.stringify(pruned) !== JSON.stringify(filters)) setFilters(pruned)
  }, [pruned]) // eslint-disable-line react-hooks/exhaustive-deps
  const update = (next) => { setFilters(next); writeParkFilters(adminKey, next) }
  const rows = sortParksLongestFirst(parks.filter((p) => parkMatchesFilters(p, pruned)))
  const asOptions = (values) => values.map((v) => ({ value: v, label: v }))
  return (
    <>
      <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }} data-testid="parked-filters">
        <FilterSelect label="Agent" allLabel="All agents" options={options.agents} value={pruned.agents} onChange={(v) => update({ ...pruned, agents: v })} testId="parked-filter-agent" />
        <FilterSelect label="Company" allLabel="All companies" options={asOptions(options.companies)} value={pruned.companies} onChange={(v) => update({ ...pruned, companies: v })} testId="parked-filter-company" />
        <FilterSelect label="Client" allLabel="All clients" options={asOptions(options.clients)} value={pruned.clients} onChange={(v) => update({ ...pruned, clients: v })} testId="parked-filter-client" />
        <Typography variant="body2" color="text.secondary" data-testid="parked-filter-count">{rows.length} of {parks.length}</Typography>
        {hasParkFilters(pruned) && (
          <Link component="button" type="button" variant="body2" onClick={() => update({ ...EMPTY_PARK_FILTERS })}>Clear filters</Link>
        )}
      </Stack>
      <Paper variant="outlined"><ParkedTable parks={rows} showParkedBy onOpen={onOpen} now={now} testId="all-parked" overdueMinutes={alertMinutes} /></Paper>
    </>
  )
}

// The Lab-only "Test shift end" field (2026-10-07): Schedule Hub is off in the
// Labs, so an admin can set a shift end here to try the end-of-shift reminder.
// Shown only when the app reports DEPLOY_ENV=lab; it feeds the reminder only.
function LabShiftEndField({ value, onChange }) {
  return (
    <TextField
      type="time" size="small" label="Test shift end (Lab only)"
      value={value} onChange={(e) => onChange?.(e.target.value)}
      InputLabelProps={{ shrink: true }} inputProps={{ 'data-testid': 'lab-shift-end' }}
      helperText="Stands in for Schedule Hub's shift end in the end-of-shift reminder."
      sx={{ mb: 2, minWidth: 220 }}
    />
  )
}

export default function ParkedView({ isAdmin, myParks = [], allParks = [], onOpen, onRefresh, scrollToAllNonce = 0, showLabShiftOverride = false, labShiftEnd = '', onLabShiftEndChange, alertMinutes = PARK_ALERT_MINUTES, adminKey = '' }) {
  const now = useNow()
  // Longest parked first in both sections (2026-10-08).
  const mine = sortParksLongestFirst(myParks)
  useEffect(() => { onRefresh?.() }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!scrollToAllNonce) return
    document.getElementById(ALL_PARKED_SECTION_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [scrollToAllNonce])

  if (!isAdmin) {
    return (
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }} data-testid="parked-view">
        <Typography variant="h6" sx={{ mb: 1 }}>My parked contacts</Typography>
        <Paper variant="outlined"><ParkedTable parks={mine} onOpen={onOpen} now={now} testId="my-parked" /></Paper>
      </Box>
    )
  }
  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }} data-testid="parked-view">
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2, borderBottom: 1, borderColor: 'divider' }}>
        {showLabShiftOverride && <LabShiftEndField value={labShiftEnd} onChange={onLabShiftEndChange} />}
        <Typography variant="h6" sx={{ mb: 1 }}>My parked contacts</Typography>
        <Paper variant="outlined"><ParkedTable parks={mine} onOpen={onOpen} now={now} testId="my-parked" /></Paper>
      </Box>
      <Box id={ALL_PARKED_SECTION_ID} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>All parked contacts</Typography>
        <AllParkedSection parks={allParks} onOpen={onOpen} now={now} alertMinutes={alertMinutes} adminKey={adminKey} />
      </Box>
    </Box>
  )
}
